import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { toast } from 'react-toastify';

const GLOBAL_API_BASE = `${process.env.REACT_APP_BACKEND_URL}synctrack/datahub-api-config`;
const TENANT_API_BASE = `${process.env.REACT_APP_BACKEND_URL}synctrack/tenant-datahub-api-config`;
const MASTER_FLOW_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/master-flow`;

const isDataHubMasterFlow = (flow) => {
  const fn = (flow.mfFlowFunction || '').toLowerCase();
  return fn.includes('/datahub/') || fn.includes('datahub');
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

/** API may return dacJson as a string or parsed object (JSONB). TextField needs a string. */
const formatDacJsonForDisplay = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return value;
    }
  }
  return String(value);
};

const normalizeDacJsonFromRow = (row) => {
  if (!row) return '';
  return formatDacJsonForDisplay(row.dacJson ?? row.dac_json);
};

const emptyForm = (headerCode) => ({
  dacName: '',
  dacApiUrl: '',
  dacApiMethod: 'GET',
  dacResponsePath: '$.results',
  dacTargetTable: '',
  dacHeaderCode: headerCode || '',
  dacSourceSystem: '',
  dacIsSecondaryToken: false,
  dacMfId: '',
  dacActive: true,
  dacSequence: 0,
  dacDescription: '',
  dacNestedArrayPath: '',
  dacParentTargetColumn: '',
  dacParentTable: '',
  dacIterateFromParent: false,
  dacExtractDual: false,
  dacChildArrayPath: '',
  dacChildTargetTable: '',
  dacChildHeaderCode: '',
  dacJson: '',
});

const DataHubApiConfigTab = ({ headerCode, readOnly = false, useTenantApi = false }) => {
  const API_BASE = useTenantApi ? TENANT_API_BASE : GLOBAL_API_BASE;
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(emptyForm(headerCode));
  const [saving, setSaving] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [masterFlows, setMasterFlows] = useState([]);

  const fetchMasterFlows = useCallback(async () => {
    try {
      const response = await fetch(MASTER_FLOW_API_URL, { headers: authHeaders() });
      if (!response.ok) return;
      const data = await response.json();
      setMasterFlows(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching master flows:', error);
    }
  }, []);

  const getMasterFlowLabel = (mfId) => {
    if (!mfId) return '';
    const match = masterFlows.find(
      (f) => f.mfId === mfId || String(f.mfId) === String(mfId)
    );
    return match ? (match.mfName || match.mfFlowFunction || mfId) : mfId;
  };

  const hubMasterFlowOptions = masterFlows.filter(isDataHubMasterFlow);
  const masterFlowSelectOptions =
    hubMasterFlowOptions.length > 0 ? hubMasterFlowOptions : masterFlows;

  const fetchConfigs = useCallback(async () => {
    if (!headerCode) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/header-code/${encodeURIComponent(headerCode)}`, {
        headers: authHeaders(),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching datahub API configs:', error);
      toast.error(`Failed to load API configs: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [headerCode]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  useEffect(() => {
    fetchMasterFlows();
  }, [fetchMasterFlows]);

  const openAdd = () => {
    setEditMode(false);
    setCurrentId(null);
    setFormData(emptyForm(headerCode));
    setDialogOpen(true);
  };

  const applyRowToForm = (row) => {
    setFormData({
      dacName: row.dacName || '',
      dacApiUrl: row.dacApiUrl || '',
      dacApiMethod: row.dacApiMethod || 'GET',
      dacResponsePath: row.dacResponsePath || '$.results',
      dacTargetTable: row.dacTargetTable || '',
      dacHeaderCode: row.dacHeaderCode || headerCode,
      dacSourceSystem: row.dacSourceSystem || '',
      dacIsSecondaryToken: !!row.dacIsSecondaryToken,
      dacMfId: row.dacMfId || '',
      dacActive: row.dacActive !== false,
      dacSequence: row.dacSequence ?? 0,
      dacDescription: row.dacDescription || '',
      dacNestedArrayPath: row.dacNestedArrayPath || '',
      dacParentTargetColumn: row.dacParentTargetColumn || '',
      dacParentTable: row.dacParentTable || '',
      dacIterateFromParent: !!row.dacIterateFromParent,
      dacExtractDual: !!row.dacExtractDual,
      dacChildArrayPath: row.dacChildArrayPath || '',
      dacChildTargetTable: row.dacChildTargetTable || '',
      dacChildHeaderCode: row.dacChildHeaderCode || '',
      dacJson: normalizeDacJsonFromRow(row),
    });
  };

  const openEdit = async (row) => {
    setEditMode(true);
    setCurrentId(row.dacId);
    applyRowToForm(row);
    setDialogOpen(true);
    try {
      const response = await fetch(`${API_BASE}/${row.dacId}`, { headers: authHeaders() });
      if (response.ok) {
        const full = await response.json();
        applyRowToForm(full);
      }
    } catch (error) {
      console.warn('Could not refresh API config by id:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleMasterFlowChange = (e) => {
    setFormData((prev) => ({ ...prev, dacMfId: e.target.value }));
  };

  const buildPayload = () => {
    const trim = (v) => (v == null ? '' : String(v).trim());
    const payload = {
      dacName: trim(formData.dacName),
      dacApiUrl: trim(formData.dacApiUrl),
      dacApiMethod: trim(formData.dacApiMethod) || 'GET',
      dacResponsePath: trim(formData.dacResponsePath) || '$.results',
      dacTargetTable: trim(formData.dacTargetTable),
      dacHeaderCode: trim(formData.dacHeaderCode) || headerCode,
      dacSourceSystem: trim(formData.dacSourceSystem) || null,
      dacIsSecondaryToken: !!formData.dacIsSecondaryToken,
      dacActive: !!formData.dacActive,
      dacSequence: Number(formData.dacSequence) || 0,
      dacDescription: trim(formData.dacDescription) || null,
      dacNestedArrayPath: trim(formData.dacNestedArrayPath) || null,
      dacParentTargetColumn: trim(formData.dacParentTargetColumn) || null,
      dacParentTable: trim(formData.dacParentTable) || null,
      dacIterateFromParent: !!formData.dacIterateFromParent,
      dacExtractDual: !!formData.dacExtractDual,
      dacChildArrayPath: trim(formData.dacChildArrayPath) || null,
      dacChildTargetTable: trim(formData.dacChildTargetTable) || null,
      dacChildHeaderCode: trim(formData.dacChildHeaderCode) || null,
      dacJson: trim(formData.dacJson) || null,
    };
    const mf = trim(formData.dacMfId);
    payload.dacCtdId = null;
    payload.dacMfId = mf || null;
    return payload;
  };

  const normalizeDacJsonForSave = (raw) => {
    const trimmed = raw == null ? '' : String(raw).trim();
    if (!trimmed) return null;
    try {
      JSON.parse(trimmed);
      return trimmed;
    } catch {
      throw new Error('Config JSON (dac_json) is not valid JSON');
    }
  };

  const handleSave = async () => {
    if (!formData.dacName?.trim() || !formData.dacApiUrl?.trim() || !formData.dacTargetTable?.trim()) {
      toast.error('Name, API URL, and target table are required');
      return;
    }
    if (!formData.dacMfId?.trim()) {
      toast.error('Master Flow is required — pick the same master flow as your Hub Flow instance');
      return;
    }
    let dacJsonPayload;
    try {
      dacJsonPayload = normalizeDacJsonForSave(formData.dacJson);
    } catch (error) {
      toast.error(error.message);
      return;
    }
    setSaving(true);
    const toastId = toast.loading(editMode ? 'Updating API config...' : 'Creating API config...');
    try {
      const url = editMode ? `${API_BASE}/${currentId}` : API_BASE;
      const method = editMode ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify({ ...buildPayload(), dacJson: dacJsonPayload }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.message || `HTTP ${response.status}`);
      }
      toast.update(toastId, {
        render: editMode ? 'API config updated' : 'API config created',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      setDialogOpen(false);
      await fetchConfigs();
    } catch (error) {
      toast.update(toastId, {
        render: `Save failed: ${error.message}`,
        type: 'error',
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const toastId = toast.loading('Deleting API config...');
    try {
      const response = await fetch(`${API_BASE}/${row.dacId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      toast.update(toastId, {
        render: 'API config deleted',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      await fetchConfigs();
    } catch (error) {
      toast.update(toastId, {
        render: `Delete failed: ${error.message}`,
        type: 'error',
        isLoading: false,
        autoClose: 5000,
      });
    }
  };

  return (
    <Box>
      {useTenantApi && !readOnly && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Tenant overrides apply at Hub Flow run time. Saving here updates the tenant copy used when you click Play.
        </Alert>
      )}
      {!readOnly && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Select the <strong>same Master Flow</strong> as on your Hub Flow instance (loaded from Master Flows).
          Connectors are assigned on the Hub Flow screen — not on this API row.
        </Alert>
      )}
      {!readOnly && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
          <Button variant="contained" size="small" startIcon={<Add />} onClick={openAdd}>
            Add API
          </Button>
        </Box>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Seq</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Target Table</TableCell>
                <TableCell>Master Flow</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Config JSON</TableCell>
                {!readOnly && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={readOnly ? 7 : 8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No API configs for this header
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                configs.map((row) => {
                  const dacJsonPreview = normalizeDacJsonFromRow(row);
                  return (
                  <TableRow key={row.dacId}>
                    <TableCell>{row.dacSequence ?? 0}</TableCell>
                    <TableCell>{row.dacName}</TableCell>
                    <TableCell>{row.dacApiMethod}</TableCell>
                    <TableCell>{row.dacTargetTable}</TableCell>
                    <TableCell>{getMasterFlowLabel(row.dacMfId)}</TableCell>
                    <TableCell>{row.dacActive ? 'Yes' : 'No'}</TableCell>
                    <TableCell>
                      {dacJsonPreview ? (
                        <Tooltip
                          title={
                            <Typography component="pre" variant="caption" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
                              {dacJsonPreview.slice(0, 1200)}
                              {dacJsonPreview.length > 1200 ? '…' : ''}
                            </Typography>
                          }
                        >
                          <Typography variant="body2" color="primary" sx={{ cursor: 'help' }}>
                            View JSON
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    {!readOnly && (
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <Button size="small" onClick={() => openEdit(row)}>
                            <Edit fontSize="small" />
                          </Button>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <Button size="small" color="error" onClick={() => handleDelete(row)}>
                            <Delete fontSize="small" />
                          </Button>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Edit' : 'Add'} Data Hub API Config</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField label="API Name" name="dacName" value={formData.dacName} onChange={handleChange} required fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Header Code" name="dacHeaderCode" value={formData.dacHeaderCode} onChange={handleChange} required fullWidth size="small" disabled />
            </Grid>
            <Grid item xs={12}>
              <TextField label="API URL" name="dacApiUrl" value={formData.dacApiUrl} onChange={handleChange} required fullWidth size="small" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Method" name="dacApiMethod" value={formData.dacApiMethod} onChange={handleChange} fullWidth size="small" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Sequence" name="dacSequence" type="number" value={formData.dacSequence} onChange={handleChange} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Response Path" name="dacResponsePath" value={formData.dacResponsePath} onChange={handleChange} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Target Table" name="dacTargetTable" value={formData.dacTargetTable} onChange={handleChange} required fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Source System" name="dacSourceSystem" value={formData.dacSourceSystem} onChange={handleChange} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel id="dac-mf-id-label">Master Flow</InputLabel>
                <Select
                  labelId="dac-mf-id-label"
                  label="Master Flow"
                  value={formData.dacMfId || ''}
                  onChange={handleMasterFlowChange}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    <em>Select master flow</em>
                  </MenuItem>
                  {masterFlowSelectOptions.map((flow) => (
                    <MenuItem key={flow.mfId} value={flow.mfId}>
                      {flow.mfName || flow.mfFlowFunction || flow.mfId}
                    </MenuItem>
                  ))}
                  {formData.dacMfId &&
                    !masterFlowSelectOptions.some((f) => String(f.mfId) === String(formData.dacMfId)) && (
                      <MenuItem value={formData.dacMfId}>
                        {getMasterFlowLabel(formData.dacMfId)} (current)
                      </MenuItem>
                    )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Description" name="dacDescription" value={formData.dacDescription} onChange={handleChange} fullWidth multiline rows={2} size="small" />
            </Grid>
            <Grid item xs={12}>
              <Divider><Typography variant="caption">Nested / parent iteration (optional)</Typography></Divider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Nested Array Path" name="dacNestedArrayPath" value={formData.dacNestedArrayPath} onChange={handleChange} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Parent Table" name="dacParentTable" value={formData.dacParentTable} onChange={handleChange} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Parent Target Column" name="dacParentTargetColumn" value={formData.dacParentTargetColumn} onChange={handleChange} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Child Header Code" name="dacChildHeaderCode" value={formData.dacChildHeaderCode} onChange={handleChange} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Child Array Path" name="dacChildArrayPath" value={formData.dacChildArrayPath} onChange={handleChange} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Child Target Table" name="dacChildTargetTable" value={formData.dacChildTargetTable} onChange={handleChange} fullWidth size="small" />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Config JSON (dac_json)"
                name="dacJson"
                value={formData.dacJson}
                onChange={handleChange}
                fullWidth
                multiline
                minRows={6}
                maxRows={16}
                size="small"
                placeholder={'{\n  "pagination": { "paginationType": "offset", "pageLimit": 100 },\n  "tokenPlacement": "header",\n  "tokenHeaderName": "Authorization"\n}'}
                helperText="Pagination, requestBody, token placement, rate limits, and Sage POST query body. Stored in datahub_api_config.dac_json."
                InputProps={{
                  sx: { fontFamily: 'Consolas, Monaco, monospace', fontSize: '0.8rem' },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Switch name="dacActive" checked={formData.dacActive} onChange={handleChange} />} label="Active" />
              <FormControlLabel control={<Switch name="dacIsSecondaryToken" checked={formData.dacIsSecondaryToken} onChange={handleChange} />} label="Secondary token (2-legged)" />
              <FormControlLabel control={<Switch name="dacIterateFromParent" checked={formData.dacIterateFromParent} onChange={handleChange} />} label="Iterate from parent" />
              <FormControlLabel control={<Switch name="dacExtractDual" checked={formData.dacExtractDual} onChange={handleChange} />} label="Extract dual" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataHubApiConfigTab;
